import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Modal,
  Text,
  Spinner,
  Alert,
  TextArea,
  Button,
} from '@cochlear-design-system/foundation';
import { PatientRecordContext } from '../contexts/PatientRecordContext.js';
import { Error as ErrorComponent } from '../../../ui/Error/Error.js';
import createProfessionalCasesService from '../../../services/createCases/createProfessionalCasesService.js';

export const ReportIssueModal = () => {
  const {
    showReportIssueModal,
    setShowReportIssueModal,
    professional,
    reportIsSubmitted,
    setReportIsSubmitted,
    getFullName,
    age,
    selectedDevice,
    selectedExpandedData,
    isDistributor,
    providerId,
    patientId,
    labels,
  } = React.useContext(PatientRecordContext);

  const [professionalCase, setProfessionalCase] = useState(undefined);

  const tryAgain = () => {
    setReportIsSubmitted(false);
  };

  const getOrganisation = () => {
    if (isDistributor) {
      return {
        distributor: {
          cochlearId: providerId,
        },
      };
    }
    return {
      provider: {
        cochlearId: providerId,
      },
    };
  };

  const getEarSide = () => {
    if (selectedDevice?.icon) {
      return selectedDevice.earLeft ? 'Left' : 'Right';
    }
    return 'Unknown';
  };

  const getSerialNo = () => {
    const { model } = selectedDevice ?? {};
    const { info } = model ?? '';
    const [, , serialNo] = info ? info.split(' ') : '';
    return serialNo;
  };

  const initPayloadData = {
    professionalId: professional.professionalId,
    caseType: 'Customer Interaction',
    subject: 'DPX Contact us',
    webReason: 'Report an implant issue',
    countryCode: professional.countryCode,
    organisation: getOrganisation(),
    email: professional.username,
    metadata: {
      modifiedBy: professional.username,
      channel: 'DPX',
    },
  };

  const initialValues = {
    description: '',
  };

  const getExtraContent = () => {
    return `
  Report an Implant issue
  Patient Details
  CochlearId: ${patientId}
  Name: ${getFullName()}
  Age: ${age}
  Device Details
  Device Type: ${selectedDevice?.device?.type}
  Serial/lot No: ${getSerialNo()}
  Ear side: ${getEarSide()}
  Surgery Date: ${
    selectedExpandedData?.expandedDataR?.[0]?.value ?? ''
  }
  Surgeon: ${selectedExpandedData?.expandedDataL?.[0]?.value ?? ''}
  Surgery Center: ${
    selectedExpandedData?.expandedDataL?.[1]?.value ?? ''
  }
  `;
  };

  const createProfessionalCasePayload = (submittedValues) => {
    const { description, ...rest } = submittedValues;
    const { webReason, ...initPayloadDataRest } = initPayloadData;
    return {
      ...rest,
      description: [getExtraContent(), description].join('\n'),
      webReason: 'Device or app support',
      ...initPayloadDataRest,
    };
  };

  const validationSchema = Yup.object().shape({
    description: Yup.string()
      .max(
        1000,
        labels[
          'labels.patientRecord.tabs.equipments.reportIssueModal.messageLimitError'
        ],
      )
      .required(
        labels[
          'labels.patientRecord.tabs.equipments.reportIssueModal.messageRequiredError'
        ],
      ),
  });

  const {
    isValid,
    dirty,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    resetForm,
    isSubmitting,
  } = useFormik({
    initialValues,
    onSubmit: async ({ description, ...rest }) => {
      const payload = createProfessionalCasePayload({
        description,
        ...rest,
      });
      const caseResult =
        await createProfessionalCasesService.createProfessionalCases(
          providerId,
          professional.professionalId,
          payload,
        );

      setReportIsSubmitted(true);
      setProfessionalCase(caseResult);
      resetForm();
    },
    validationSchema,
    validateOnBlur: true,
    validateOnChange: true,
  });

  useEffect(() => {
    if (showReportIssueModal) {
      resetForm();
    }
  }, [showReportIssueModal]);

  return (
    <Modal
      show={showReportIssueModal}
      onHide={() => setShowReportIssueModal(false)}
      size="xl"
      centered
      backdrop="static"
      keyboard={false}
      variant="primary"
    >
      <Modal.Header
        closeButton
        data-analytics="cancel_report_issue_modal"
      >
        <Modal.Title>
          {
            labels[
              'labels.patientRecord.tabs.equipments.reportIssueModal.title'
            ]
          }
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {reportIsSubmitted && professionalCase?.success && (
          <Alert
            heading={
              labels[
                'labels.patientRecord.tabs.equipments.reportIssueModal.messageHasBeenSent'
              ]
            }
            show
            variant="success"
          >
            <Text
              content={
                labels[
                  'labels.patientRecord.tabs.equipments.reportIssueModal.messageSentSuccessOne'
                ]
              }
              type="body-text"
            />
            <br />
            <Text
              content={labels[
                'labels.patientRecord.tabs.equipments.reportIssueModal.messageSentSuccessTwo'
              ].replace(
                '[CASE_NUMBER]',
                `${professionalCase.data.id}`,
              )}
              type="body-text"
            />
          </Alert>
        )}
        {reportIsSubmitted && !professionalCase && (
          <ErrorComponent
            config={{
              buttons: [
                {
                  action: 'link-dpx-ri',
                  icon: '',
                  link: '',
                  name: 'dpxri',
                  text: `${labels['labels.patientRecord.tabs.equipments.reportIssueModal.tryAgain']}`,
                  useCallback: true,
                  variant: 'brand-primary',
                },
              ],
              codeLabel: `${labels['labels.patientRecord.tabs.equipments.reportIssueModal.errorMessageCodeLabel']}`,
              handleCallback: () => {
                tryAgain();
              },
              imgAlt: 'labels.errorMessage.imgAlt',
              imgSrc:
                'https://mss-p-007-delivery.sitecorecontenthub.cloud/api/public/content/9bfd65ed69994fa49be3ff1339ea9674?v=cc7c6ec3',
              imgWidth: 116,
              text: 'labels.errorMessage.text',
              title: 'labels.errorMessage.title',
            }}
            data={{}}
            labels={{
              'labels.errorMessage.codeLabel': `${labels['labels.patientRecord.tabs.equipments.reportIssueModal.errorMessageCodeLabel']}`,
              'labels.errorMessage.imgAlt': `${labels['labels.patientRecord.tabs.equipments.reportIssueModal.errorMessageImgAlt']}`,
              'labels.errorMessage.text': `${labels['labels.patientRecord.tabs.equipments.reportIssueModal.errorMessageBody']}`,
              'labels.errorMessage.title': `${labels['labels.patientRecord.tabs.equipments.reportIssueModal.errorMessageTitle']}`,
            }}
          />
        )}
        {!reportIsSubmitted && (
          <>
            <div className="report-issue-group">
              <div className="ccl__columns">
                <div className="ccl__column">
                  <Text
                    content={`${labels['labels.patientRecord.tabs.equipments.reportIssueModal.reasonForContacting']}`}
                    type="body-text-bold"
                  />
                </div>
                <div className="ccl__column">
                  <Text
                    content={`${initPayloadData.webReason}`}
                    type="small-body-text"
                  />
                </div>
                <div className="ccl__column" />
              </div>
            </div>

            <div className="report-issue-group">
              <div className="ccl__columns report-issue-section-title">
                <div className="ccl__column">
                  <Text
                    content={`${labels['labels.patientRecord.tabs.equipments.reportIssueModal.patientDetails']}`}
                    type="body-text-bold"
                  />
                </div>
              </div>
              <div className="ccl__columns">
                <div className="ccl__column">
                  <Text
                    content={`${
                      labels[
                        'labels.patientRecord.tabs.equipments.reportIssueModal.name'
                      ]
                    }: ${getFullName()}`}
                    type="small-body-text"
                  />
                </div>
                <div className="ccl__column">
                  <Text
                    content={`${labels['labels.patientRecord.tabs.equipments.reportIssueModal.age']}: ${age}`}
                    type="small-body-text"
                  />
                </div>
                <div className="ccl__column" />
              </div>
            </div>

            <div className="report-issue-group">
              <div className="ccl__columns report-issue-section-title">
                <div className="ccl__column">
                  <Text
                    content={`${labels['labels.patientRecord.tabs.equipments.reportIssueModal.deviceDetails']}`}
                    type="body-text-bold"
                  />
                </div>
              </div>
              <div className="ccl__columns report-issue-section-row">
                <div className="ccl__column">
                  <Text
                    content={`${labels['labels.patientRecord.tabs.equipments.reportIssueModal.deviceType']}: ${selectedDevice?.device?.type}`}
                    type="small-body-text"
                  />
                </div>
                <div className="ccl__column">
                  <Text
                    content={`${
                      labels[
                        'labels.patientRecord.tabs.equipments.reportIssueModal.serialNo'
                      ]
                    }: ${getSerialNo()}`}
                    type="small-body-text"
                  />
                </div>
                <div className="ccl__column">
                  <Text
                    content={`${
                      labels[
                        'labels.patientRecord.tabs.equipments.reportIssueModal.earSide'
                      ]
                    }: ${getEarSide()}`}
                    type="small-body-text"
                  />
                </div>
              </div>
              {!selectedExpandedData && (
                <>
                  <div className="ccl__columns report-issue-section-row ">
                    <div
                      className="ccl__column"
                      style={{ textAlign: 'center' }}
                    >
                      <Spinner animation="border" />
                      <Text
                        content={`${labels['labels.patientRecord.tabs.equipments.reportIssueModal.loadingSurgeryDetails']}`}
                        type="small-body-text"
                      />
                    </div>
                  </div>
                </>
              )}
              {selectedExpandedData && (
                <>
                  <div className="ccl__columns report-issue-section-row">
                    <div className="ccl__column">
                      <Text
                        content={`${
                          labels[
                            'labels.patientRecord.tabs.equipments.reportIssueModal.surgeryDate'
                          ]
                        }: ${
                          selectedExpandedData?.expandedDataR?.[0]
                            ?.value ?? ''
                        }`}
                        type="small-body-text"
                      />
                    </div>
                    <div className="ccl__column">
                      <Text
                        content={`${
                          labels[
                            'labels.patientRecord.tabs.equipments.reportIssueModal.surgeon'
                          ]
                        }: ${
                          selectedExpandedData?.expandedDataL?.[0]
                            ?.value ?? ''
                        }`}
                        type="small-body-text"
                      />
                    </div>
                    <div className="ccl__column" />
                  </div>

                  <div className="ccl__columns">
                    <div className="ccl__column">
                      <Text
                        content={`${
                          labels[
                            'labels.patientRecord.tabs.equipments.reportIssueModal.surgeryCenter'
                          ]
                        }: ${
                          selectedExpandedData?.expandedDataL?.[1]
                            ?.value ?? ''
                        }`}
                        type="small-body-text"
                      />
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="report-issue-form-wrapper">
              <form onSubmit={handleSubmit} onChange={handleChange}>
                <div className="report-issue-form-description-wrapper">
                  <TextArea
                    isFluid
                    label={
                      labels[
                        'labels.patientRecord.tabs.equipments.reportIssueModal.message'
                      ]
                    }
                    name="description"
                    stateManaged={false}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={
                      touched.description && !!errors.description
                    }
                    errorMsg={errors.description}
                  />
                </div>
              </form>
              <div className="report-issue-from-wrapper">
                <Text
                  content={
                    labels[
                      'labels.patientRecord.tabs.equipments.reportIssueModal.from'
                    ]
                  }
                  type="body-text-bold"
                />
                <Text
                  content={`${professional.firstName} ${professional.lastName}`}
                  type="small-body-text"
                />
                <Text
                  content={`${professional.username}`}
                  type="small-body-text"
                />
              </div>
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer className="report-issue-modal-footer">
        {reportIsSubmitted && professionalCase && (
          <>
            <Button
              onClick={() => {
                setShowReportIssueModal(false);
              }}
              text={
                labels[
                  'labels.patientRecord.tabs.equipments.reportIssueModal.close'
                ]
              }
              variant="secondary"
              data-analytics="close_customer_case"
            />
          </>
        )}
        {!reportIsSubmitted && (
          <>
            <Button
              onClick={handleSubmit}
              text={
                labels[
                  'labels.patientRecord.tabs.equipments.reportIssueModal.sendEnquiry'
                ]
              }
              disabled={!dirty || !isValid || isSubmitting}
              data-analytics="send_customer_case"
            />
            <Button
              onClick={() => {
                setShowReportIssueModal(false);
              }}
              text={
                labels[
                  'labels.patientRecord.tabs.equipments.reportIssueModal.cancel'
                ]
              }
              variant="tertiary"
              data-analytics="cancel_customer_case"
            />
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};
